import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import RfqQuoteDetailsQuotedPricing, {
    RfqQuoteDetailsQuotedPricingStyles,
} from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { validateQuantityField } from "@ucs/Helpers/FormFieldsValidation";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { ChangeEvent, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    extendedStyles?: RfqQuoteDetailsProposedDetailsGridItemStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateQuoteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsProposedDetailsGridItemStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    quoteLineInfoGridItem?: GridItemProps;
    quoteLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    productPrice?: ProductPriceStyles;
    quotedPricingGridItem?: GridItemProps;
    quotedPricingText?: TypographyPresentationProps;
    quotedPricingStyles?: RfqQuoteDetailsQuotedPricingStyles;
    quantityAndSubtotalGridItem?: GridItemProps;
    quantityAndSubtotalContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantityTextField?: TextFieldPresentationProps;
    subtotalGridItem?: GridItemProps;
    subtotalLabel?: TypographyPresentationProps;
    subtotalText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    notesAndCostCodeGridItem?: GridItemProps;
    notesAndCostCodesContainer?: GridContainerProps;
    notesGridItem?: GridItemProps;
    toggleNotesLink?: LinkPresentationProps;
    notesTextArea?: TextAreaProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
}

export const rfqQuoteDetailsProposedDetailsGridItemStyles: RfqQuoteDetailsProposedDetailsGridItemStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: [3, 3, 2, 2, 2],
    },
    quoteLineInfoGridItem: {
        width: [9, 9, 10, 10, 10],
    },
    quoteLineInfoContainer: {
        gap: 20,
    },
    infoLeftColumn: {
        width: [12, 12, 12, 12, 6],
        css: css`
        padding-top:0px;
        `,
    },
    infoLeftColumnContainer: {
        gap: 20,
        css: css`
        > div[class^="GridOffset-sc"] {margin-left:5px;}
    `,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            padding-top:0px;
            margin-left: 5px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css`
                        padding-left: 0px;
                    `,
                ]);
            }}
        `,
        width: 12,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: fit-content;
                box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
            `,
        },
        img: {
            css: css`
                height: 120px;
                width: 120px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: 80px;
                            width: 80px;
                        `,
                        css`
                            height: 80px;
                            width: 80px;
                        `,
                        css`
                            height: 100px;
                            width: 100px;
                        `,
                        css`
                            height: 100px;
                            width: 100px;
                        `,
                    ]);
                }}
            `,
        },
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                transform: "uppercase",
                css: css`
                    font-size: 13px;
                    color: ${UCSColors.gray04} !important;
                    line-height: 16px;
                    margin-bottom: 7px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                            css`
                                font-size: 12px;
                                margin-bottom: 2px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: 18,
                color: UCSColors.gray00,
                weight: 500,
                lineHeight: "20px",
                css: css`
                    margin-bottom: 8px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                            css`
                                font-size: 16px;
                                line-height: 18px;
                            `,
                        ]);
                    }}
                `,
            },
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                min-width: 200px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                    ]);
                }}
            `,
        },
        erpNumberLabelText: {
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 0;
                min-width: fit-content;
            `,
        },
        erpNumberValueText: {
            css: css`
                min-width: fit-content;
            `,
        },
        customerNameGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        customerNameLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        manufacturerItemLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
    },
    quotedPricingStyles: {
        row: {
            evenRowCss: css`
            background: ${getColor("common.background")};
            `,
        },
        table: {
            css: css`
            border-collapse: separate;
            border-spacing: 0 0.5rem;
            `,
        },
    },
    productPrice: {
        wrapper: {
            css: css`
                margin-top: 10px;
            `,
        },
        priceLabelText: {
            size: 13,
            weight: 700,
            color: UCSColors.gray04,
            transform: "uppercase",
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
                margin-bottom: 5px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
        price: {
            priceText: {
                css: css`
                    background: ${getColor("common.accent")};
                    padding: 5px 10px 5px 0px;
                `,
            },
        },
    },
    quotedPricingGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                        padding-left: 20px;
                    `,
                        css`
                        padding-left: 20px;
                    `,
                    ],
                    "max",
                );
            }}
            flex-direction: column;
        `,
    },
    quotedPricingText: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    quantityAndSubtotalGridItem: {
        width: [12, 12, 12, 12, 6],
        css: css`
            padding-top:0px;
        `,
    },
    quantityAndSubtotalContainer: {
        gap: 20,
    },
    quantityGridItem: {
        width: [4, 4, 4, 4, 4],
        printWidth: 10,
        css: css`
            padding-top:0px;
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                        padding-left: 20px;
                    `,
                        css`
                        padding-left: 20px;
                    `,
                    ],
                    "max",
                );
            }}
        `,
    },
    subtotalGridItem: {
        width: [6, 6, 6, 6, 6],
        align: "bottom",
        printWidth: 12,
        css: css`
            margin-top: 1px !important;
            white-space:no-wrap;
            margin-left:30px !important;
            padding-top: 0px;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            margin-top: 5px;
            gap: 15px;

            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    quantityTextField: {
        labelProps: {
            size: 13,
            weight: 700,
        },
        cssOverrides: {
            inputSelect: css`
                height: 33px;
            `,
        },
    },
    subtotalLabel: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    subtotalText: {
        weight: 700,
        css: css`
            background: ${getColor("common.background")};
            white-space: nowrap;
            padding: 5px 15px 5px 15px;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    notesAndCostCodeGridItem: {
        width: 12,
        css: css`
        ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                        padding-left: 20px;
                    `,
                        css`
                        padding-left: 20px;
                    `,
                    ],
                    "max",
                );
            }}`
    },
    notesAndCostCodesContainer: {
        gap: 20,
    },
    notesGridItem: {
        width: [12, 12, 12, 11, 11],
        css: css`
            flex-direction: column;
        `,
    },
    toggleNotesLink: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    costCodeGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    notesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
        `,
    },
};

const styles = rfqQuoteDetailsProposedDetailsGridItemStyles;

const RfqQuoteDetailsProposedDetailsGridItem = ({
    quote,
    quoteLine,
    enableVat,
    vatPriceDisplay,
    showPricing,
    updateQuoteLine,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [quantityError, setQuantityError] = useState("");

    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);
        if (Number.isNaN(inputQty)) {
            return;
        }

        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                qtyOrdered: inputQty,
            },
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quantity updated"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const {
        value: qtyOrdered,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(quoteLine.qtyOrdered!.toString(), qtyOrderedSubmitHandler);

    const [showNotes, setShowNotes] = useState(false);
    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const [notes, setNotes] = useState(quoteLine.notes);

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = () => {
        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                notes,
            },
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Notes updated"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };


    React.useEffect(() => {
        if (typeof qtyOrdered !== "undefined") {
            validateQuantity();
        }
    }, [qtyOrdered]);

    const validateQuantity = () => {
        const errorMessage = validateQuantityField(qtyOrdered);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Line Notes")
        : quoteLine.notes.length === 0
            ? translate("Add Line Notes")
            : translate("Show Line Notes");

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={quoteLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.quoteLineInfoGridItem}>
                <GridContainer {...styles.quoteLineInfoContainer}>
                    <GridItem {...styles.infoLeftColumn}>
                        <GridContainer {...styles.infoLeftColumnContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {quoteLine.brand && (
                                    <ProductBrand brand={quoteLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={quoteLine} extendedStyles={styles.productDescription} />
                                {/* UFG-328 */}
                                <Typography>
                                    {quoteLine.altText.trim()}
                                </Typography>
                                {/* UFG-328 */}
                                <ProductPartNumbers
                                    productNumber={quoteLine.erpNumber}
                                    customerProductNumber={quoteLine.customerName}
                                    manufacturerItem={quoteLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                                <ProductPrice
                                    product={quoteLine}
                                    currencySymbol={quote.currencySymbol}
                                    showLabel={true}
                                    extendedStyles={styles.productPrice}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.quotedPricingGridItem}>
                        {showPricing && (
                            <>
                                <Typography {...styles.quotedPricingText}>{translate("Quoted Pricing")}</Typography>
                                <RfqQuoteDetailsQuotedPricing
                                    quote={quote}
                                    quoteLine={quoteLine}
                                    extendedStyles={styles.quotedPricingStyles}
                                />
                            </>
                        )}
                    </GridItem>
                    <GridItem {...styles.quantityAndSubtotalGridItem}>
                        <GridContainer {...styles.quantityAndSubtotalContainer}>
                            <GridItem {...styles.quantityGridItem}>
                                <TextField
                                    {...styles.quantityTextField}
                                    type="number"
                                    min={0}
                                    defaultValue="1"
                                    error={quantityError}
                                    label={translate("QTY")}
                                    value={qtyOrdered}
                                    disabled={quoteLine.isPromotionItem || quote.isJobQuote}
                                    onChange={qtyOrderedChangeHandler}
                                    onKeyDown={qtyOrderedKeyDownHandler}
                                    onBlur={qtyOrderedBlurHandler}
                                    data-test-selector="quoteDetail_productQuantity"
                                />
                            </GridItem>
                            <GridItem
                                {...styles.subtotalGridItem}
                                data-test-selector={`subTotal_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
                            >
                                {quoteLine.pricing && showPricing && (
                                    <>
                                        <VisuallyHidden>{translate("Subtotal")}</VisuallyHidden>
                                        <Typography {...styles.subtotalLabel}>{translate("Total")}</Typography>
                                        <Typography {...styles.subtotalText}>
                                            {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? quoteLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                : quoteLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        {enableVat && (
                                            <>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {vatPriceDisplay === "DisplayWithVat" ||
                                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                        ? `${translate("Inc. VAT")} (${quoteLine.pricing.vatRate}%)`
                                                        : translate("Ex. VAT")}
                                                </Typography>
                                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                    <>
                                                        <Typography {...styles.subtotalWithoutVatText}>
                                                            {quoteLine.pricing.extendedUnitNetPriceDisplay}
                                                        </Typography>
                                                        <Typography as="p" {...styles.vatLabelText}>
                                                            {translate("Ex. VAT")}
                                                        </Typography>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </GridItem>
                            <GridItem {...styles.quotedPricingGridItem}>
                                <Typography {...styles.quotedPricingText}>{translate("Quoted Pricing")}</Typography>
                                <RfqQuoteDetailsQuotedPricing
                                    quote={quote}
                                    quoteLine={quoteLine}
                                    extendedStyles={styles.quotedPricingStyles}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {quote.showLineNotes && !quoteLine.isPromotionItem && (
                        <GridItem {...styles.notesAndCostCodeGridItem}>
                            <GridContainer {...styles.notesAndCostCodesContainer}>
                                <GridItem {...styles.notesGridItem}>
                                    <Link {...styles.toggleNotesLink} onClick={toggleNotesClickHandler}>
                                        {toggleNotesHeading}
                                    </Link>
                                    {showNotes && (
                                        <TextArea
                                            {...styles.notesTextArea}
                                            value={notes}
                                            disabled={!quote.canModifyOrder}
                                            onChange={notesChangeHandler}
                                            onBlur={notesBlurHandler}
                                        />
                                    )}
                                </GridItem>
                                <GridItem {...styles.costCodeGridItem}>
                                    {quote.showCostCode && (
                                        <>
                                            <Typography {...styles.costCodeLabelText}>{quote.costCodeLabel}</Typography>
                                            {quote.canEditCostCode ? (
                                                <Select {...styles.costCodeSelect}>
                                                    {quote.costCodes?.map(costCode => (
                                                        <option key={costCode.costCode} value={costCode.costCode}>
                                                            {costCode.description}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <Typography {...styles.costCodeText}>{quoteLine.costCode}</Typography>
                                            )}
                                        </>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsProposedDetailsGridItem);
